import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Bell, AlertTriangle, ArrowUpRight, Send } from "lucide-react";
import { toast } from "sonner";

export default function RemindersCard() {
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [r, c] = await Promise.all([api.get("/reminders"), api.get("/email/config")]);
      setData(r.data); setConfig(c.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);
  if (!data) return null;
  if (data.total_count === 0) return null;

  const sendDigest = async () => {
    if (!config?.configured) {
      toast.error("Email not configured yet — add RESEND_API_KEY to backend/.env first.");
      return;
    }
    setSending(true);
    try {
      const { data: r } = await api.post("/reminders/send", {});
      if (r.sent) toast.success(`Reminder digest emailed to ${r.to}`);
      else toast.message("Nothing to send right now.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send digest");
    } finally {
      setSending(false);
    }
  };

  const overdueCount = (data.counts.overdue_maintenance || 0) + (data.counts.overdue_care || 0);
  const upcomingCount = (data.counts.upcoming_maintenance || 0) + (data.counts.upcoming_care || 0);

  return (
    <div className="border-2 border-[#C77E1A] bg-[#FFF6E5] p-4 mb-6" data-testid="reminders-card">
      <div className="flex items-start gap-3">
        <Bell className="w-5 h-5 text-[#C77E1A] mt-0.5 shrink-0" strokeWidth={2.2} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 flex-wrap">
            <div className="font-display text-base font-black uppercase tracking-wider text-[#5C4A2B]">
              {data.total_count} reminder{data.total_count === 1 ? "" : "s"}
            </div>
            <div className="flex items-center gap-3 text-[11px] text-[#5C4A2B]">
              {overdueCount > 0 && (
                <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {overdueCount} overdue</span>
              )}
              {upcomingCount > 0 && <span>{upcomingCount} due in 7 days</span>}
            </div>
          </div>

          <ul className="mt-3 space-y-1 text-xs" data-testid="reminders-list">
            {data.overdue_maintenance.slice(0, 4).map((r) => (
              <li key={r.id} className="flex items-baseline gap-2">
                <span className="bg-[#BC4727] text-white text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 shrink-0">OVERDUE {r.days_overdue}d</span>
                <span className="font-semibold truncate">{r.title}</span>
                <span className="text-[#66645E] truncate">— {r.equipment_name}</span>
              </li>
            ))}
            {data.upcoming_maintenance.slice(0, 3).map((r) => (
              <li key={r.id} className="flex items-baseline gap-2">
                <span className="bg-[#5C4A2B] text-white text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 shrink-0">In {r.days_until}d</span>
                <span className="font-semibold truncate">{r.title}</span>
                <span className="text-[#66645E] truncate">— {r.equipment_name}</span>
              </li>
            ))}
            {data.upcoming_care.slice(0, 3).map((r) => (
              <li key={r.id} className="flex items-baseline gap-2">
                <span className="bg-[#1A4135] text-white text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 shrink-0">{(r.event_type || "care").toUpperCase()} in {r.days_until}d</span>
                <span className="truncate">{r.description || "—"}</span>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Link to="/maintenance" data-testid="reminders-open-maintenance" className="text-[#5C4A2B] hover:underline inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider">
              Open maintenance <ArrowUpRight className="w-3 h-3" />
            </Link>
            <span className="text-[#5C4A2B] opacity-30">·</span>
            <Link to="/livestock" data-testid="reminders-open-livestock" className="text-[#5C4A2B] hover:underline inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider">
              Open livestock <ArrowUpRight className="w-3 h-3" />
            </Link>
            <button
              type="button"
              data-testid="reminders-send-digest"
              onClick={sendDigest}
              disabled={sending || !config?.configured}
              title={config?.configured ? "Send all reminders to your email" : "Email not configured yet"}
              className="ml-auto border border-[#5C4A2B] text-[#5C4A2B] px-3 py-1 text-[10px] font-bold uppercase tracking-wider hover:bg-[#5C4A2B] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
            >
              <Send className="w-3 h-3" /> {sending ? "Sending…" : "Email Digest"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
